import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ChevronDownIcon, ChevronUpIcon, Hint, Text } from '@common-fe/common-fe';

import { Option } from '@/modules/core/components/Menu/menu.types';
import theme from '@/styles/theme';

import MobileSidebarOption from './MobileSidebarOption/MobileSidebarOption';
import { Delimiter, StyledText, SubItem, Title, TitleWrapper, Wrapper } from './SidebarOption.styles';

export type Props = Option & {
  testId?: string;
  active?: boolean;
  path: string;
  currentKey: string;
  onPick: (url?: string) => void;
  minify: boolean;
};

const MAX_TITLE_LENGTH = 18;

const SidebarOption: React.FC<Props> = ({
  testId,
  title,
  icon,
  path,
  delimiter,
  currentKey,
  onPick,
  options,
  minify,
  url,
  link,
  textStyle,
  dropdownOptions,
  isNotClickable,
  additionalIcon,
  disabled,
  hintComponent,
  isMobileMode,
  withTip,
  node,
  wrapperStyle,
}) => {
  const [expand, setExpanded] = useState(false);
  const isOptions = useMemo(() => options && options.length, [options]);
  const handleClick = useCallback(() => {
    if (link) {
      window.open(link, '_blank');
      return;
    }
    if (isOptions) {
      setExpanded(true);
      // return;
    }
    onPick(url);
  }, [link, isOptions, onPick, url]);
  const subItemsList = useMemo(() => (options ? options.map((item) => item.url) : []), [options]);

  useEffect(() => {
    if (options?.length) {
      if (expand) {
        if (!subItemsList.includes(path) && url !== path) {
          setExpanded(false);
        }
      }
      if (!expand) {
        if (subItemsList.includes(path)) {
          setExpanded(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, path]);

  const active = useMemo(() => {
    if (dropdownOptions) {
      return !!dropdownOptions.find((item) => item.url === path);
    }
    if (!url) {
      return false;
    }
    if (path === url) {
      return true;
    }
    if (options) {
      return !!options.find((item) => item.url === path);
    }
    return false;
  }, [path, url, options, dropdownOptions]);

  if (delimiter) {
    if (minify) {
      return null;
    }
    return <Delimiter key={currentKey}>{title}</Delimiter>;
  }

  if (isMobileMode) {
    return (
      <MobileSidebarOption
        testId={testId}
        disabled={disabled}
        active={active}
        icon={icon}
        onClick={handleClick}
        onPick={onPick}
        dropdownOptions={dropdownOptions}
      />
    );
  }

  return (
    <div>
      <Hint
        hintWidth={{ width: 'auto' }}
        hideHint={!hintComponent}
        hintAlign="right"
        hintElement={
          <Box>
            <Wrapper
              minify={minify}
              // activeBackgroundOnHover={activeBackgroundOnHover}
              active={active || expand}
              {...(disabled || isNotClickable
                ? {
                    style: {
                      cursor: isNotClickable ? 'default' : 'pointer',
                    },
                  }
                : {
                    onClick: handleClick,
                    style: active || minify ? undefined : wrapperStyle,
                  })}
              disabled={disabled}
              aria-label={title}
              data-testid={`Sidebar-option-${title}`}
            >
              {icon}

              <TitleWrapper direction="row" align="center">
                <Hint
                  hintWidth={{ width: 'auto' }}
                  hideHint={!withTip}
                  hintAlign="right"
                  hintElement={
                    <Title
                      minify={minify}
                      active={active || expand}
                      style={disabled ? { color: theme.colors.textDisabled } : textStyle || undefined}
                    >
                      {node ?? title}
                    </Title>
                  }
                >
                  <Text size="small" color="textOnColor">
                    {title}
                  </Text>
                </Hint>
                {additionalIcon && !minify && additionalIcon}
              </TitleWrapper>

              {!!isOptions && !minify && (
                <>
                  {expand ? (
                    <ChevronUpIcon size="24px" color="eeeeeeee" />
                  ) : (
                    <ChevronDownIcon size="24px" color="textBody" />
                  )}
                </>
              )}
            </Wrapper>
            {!!isOptions &&
              !!expand &&
              !minify &&
              options &&
              options.map((item) => (
                <Hint
                  key={item.key}
                  hintWidth={{ width: 'auto' }}
                  hintAlign="right"
                  hideHint={item?.title?.length <= MAX_TITLE_LENGTH}
                  hintElement={
                    <SubItem
                      pad={{ right: 'l', left: 'xs' }}
                      margin={{ vertical: 'spacing6', left: 'xxl' }}
                      key={item.key}
                      onClick={() => {
                        onPick(item.url);
                      }}
                      active={path === item.url}
                    >
                      <StyledText>{item.title}</StyledText>
                    </SubItem>
                  }
                >
                  <Text size="small" color="textOnColor">
                    {item.title}
                  </Text>
                </Hint>
              ))}
          </Box>
        }
        disabled={disabled}
      >
        {hintComponent}
      </Hint>
    </div>
  );
};

export default SidebarOption;
